import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import {
  ChipExpression,
  QueryExpressionParserService
} from "@nrwl/query-emitter";
import { Chip } from "../models/chip";
import { SuggestionsSearchProvider } from "../../suggestions.search.provider";
import { AutocompleteOffer } from "../../common.data.types";
import { endsWith, forEach, get, last, trim } from 'lodash';
import { IPHelper } from "@nrwl/common-lib";

const hostKeyword = "host";

@Injectable()
export class AutoSuggestService {
  rules = {
    forTerm: true
  };
  dataSource: Observable<Array<ChipExpression>>;

  constructor(
    readonly queryEngine: QueryExpressionParserService,
    private readonly autocompleteService: SuggestionsSearchProvider
  ) {}

  setRules(rules) {
    this.rules.forTerm = get(rules, "forTerm", true);
  }

  set(userInput) {
    this.dataSource = this.autocompleteUserInput(userInput);
  }

  mapKeywordToCategory(keyword: string): string {
    if (!keyword) {
      return keyword;
    }
    if (keyword.toLowerCase() === "app") {
      return "application";
    }
    // right now, mapping is more than trivial - same keyword = same category. Definitely will change in the future/once
    // backend DB with autocompletes gets populated with something more reasonable
    return keyword.toLowerCase();
  }

  mapCategoryToKeyword(category: string): string {
    if (category === "application") {
      return "app";
    }
    return category;
  }

  private _mapResponse(userInput) {
    return map((response: any) => {
      const result = [];

      if (IPHelper.isValidIP(last(userInput.operands))) {
        result.push(new Chip(hostKeyword, userInput.operator, userInput.operands, hostKeyword));
      }

      forEach(response, searchItem => {
        const keyword = this.mapCategoryToKeyword(searchItem.category);
        result.push(
          new Chip(keyword, userInput.operator, [searchItem.value], keyword)
        );
      });

      return result;
    });
  }

  buildAutoCompleteForCategory(valueStr, userInput) {
    return this.autocompleteService
      .searchForSuggestionsInCategory(
        this.mapKeywordToCategory(userInput.keyword),
        valueStr
      )
      .pipe(this._mapResponse(userInput));
  }

  buildAutocompleteForKeywords(userInput: ChipExpression): Chip[] {
    return [];
  }

  getLastOperand(userInput): string {
    if (userInput.operands) {
      return (Array.isArray(userInput.operands)) ?
        userInput.operands[userInput.operands.length - 1] :
        userInput.operands;
    }
    return null;
  }

  getAllowedCategories(): string[] {
    const keywords = this.queryEngine.getChipKeywords();
    const result = [];
    for (const keyword of keywords ? keywords : []) {
      result.push(this.mapKeywordToCategory(keyword));
    }
    return result;
  }

  buildAutoCompleteForTerm(valueStr, userInput) {
    const allowedCategories = this.getAllowedCategories();
    return this.autocompleteService
      .searchForSuggestions(valueStr, allowedCategories)
      .pipe(this._mapResponse(userInput));
  }

  buildAutocompleteForOperators(userInput: ChipExpression) {
    const operators = this.queryEngine.getChipOperators();
    const cList = [];
    operators.forEach(operator => {
      cList.push(new AutocompleteOffer(operator, operator, userInput));
    });
    return cList;
  }

  autocompleteUserInput(text: string): Observable<any> {
    let currentInput: string = trim(text);

    if (!currentInput) {
      return of(this.buildAutocompleteForKeywords(new ChipExpression()));
    }

    const pieces: Array<string> = currentInput.split('=').map(s => s.trim());

    if(pieces.length > 1) {
      currentInput = pieces.shift() + '="' + pieces.join('') + '"';
    }

    const userInput = this.queryEngine.parseChipIntoTokens(currentInput);

    if (!userInput) {
      // autocompletion not possible as user started typing nonsense
      // but nevertheless, lets throw the input at the elasticsearch
      // in a hope it will figure out something..
      return this.buildAutoCompleteForTerm(currentInput, currentInput);
      // return of([]);
    }
    // I saw examples were user types 'lloccation' - which is interpreted as 'operand' without keyword or operator
    // learn to deal with that.
    if (
      this.rules.forTerm &&
      !userInput.keyword &&
      !userInput.operator &&
      this.getLastOperand(userInput)
    ) {
      return this.buildAutoCompleteForTerm(
        this.getLastOperand(userInput),
        userInput
      );
    }

    // unfortunately, this IF will never be evaluated to 'true' condition. Why?
    // the open-ended grammar file will treat unrecognized input as plain 'operand' in order to capture the text and try to match it
    // against one of 3 fields (keyword, operator, operand).
    // as result, previous IF will always assume that input 'loc' is not a prefix for keyword input/autocompleter,
    // but a value for term lookup in backend/autocomplete service.
    // For the moment, I believe this is preferred way of operation, so leaving things 'as is'.

    if (!userInput.keyword) {
      return of(this.buildAutocompleteForKeywords(userInput));
    }
    // if (!userInput.operator) {
    //   return of(this.buildAutocompleteForOperators(userInput));
    // }

    //return autocomplete by category only for supported keywords
    if(this.queryEngine.areChipKeywordValid(userInput)) {
      // extract last entered operand
      const valueStr = this.getLastOperand(userInput);

      if (!valueStr) {
        // now we have possibility to show values from this category
        // return this.buildAutoCompleteForCategory(null, userInput);
        return this.buildAutoCompleteForCategory(null, userInput);
      }
      // before we decide to show popup, check if we must do that.
      // if user already entered (or chose in autocompleter) the operand, we should not display
      // popup again - until the user enters ','+ at least one character
      // if (endsWith(trim(this.chipInput.inputText), '"')) {
      //   return of([]);
      // }
      return this.buildAutoCompleteForCategory(valueStr, userInput);
    }
  }
}
